<template>
  <div class="waper-opinions-page container">
    <div>
      <TheBreadcrumb :pages="arianeRoute" />
      <HeaderCommunity
        :page="currentPageInfo"
        :dark-mode="true"
        action-label="Ajouter un avis"
        review
      />
      <div class="bazar">
        <div class="bazar-voice">
          <p class="dark">Avis authentiques</p>
          <div class="light">
            <img
              src="~assets/icons/bazarvoice.svg"
              alt="bazar voice"
            />
          </div>
        </div>
        <HeaderBazarVoice
          v-if="communityStats"
          :community="communityStats"
        />
      </div>
      <SearchResults>
        <template #search>
          <div class="title-line">
            <div class="mode">
              <p>Mode d'affichage</p>
              <div
                class="list mode-button"
                :class="{ active: viewMode == 'list' }"
                @click="viewMode = 'list'"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16.609 13.191"
                >
                  <g
                    transform="translate(-292.477 -886.404)"
                    fill="none"
                    stroke="#959595"
                  >
                    <line
                      x2="16.609"
                      transform="translate(292.477 886.904)"
                    />
                    <line
                      x2="16.609"
                      transform="translate(292.477 893)"
                    />
                    <line
                      x2="16.609"
                      transform="translate(292.477 899.096)"
                    />
                  </g>
                </svg>
              </div>
              <div
                class="bloc mode-button"
                :class="{ active: viewMode == 'card' }"
                @click="viewMode = 'card'"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16.762 16.762"
                >
                  <rect
                    width="7.619"
                    height="7.619"
                    fill="#959595"
                  />
                  <rect
                    width="7.619"
                    height="7.619"
                    fill="#959595"
                    transform="translate(0 9.143)"
                  />
                  <rect
                    width="7.619"
                    height="7.619"
                    fill="#959595"
                    transform="translate(9.143)"
                  />
                  <rect
                    width="7.619"
                    height="7.619"
                    fill="#959595"
                    transform="translate(9.143 9.143)"
                  />
                </svg>
              </div>
            </div>
            <div class="options">
              <InputSearch
                v-model="search"
                :is-loading="isLoading"
                :min-length="3"
                placeholder-text="Rechercher un avis de joueur"
              />
              <div class="sort">
                <select
                  v-model="sortReview"
                  class="c-form__select"
                  @change="onSortChange"
                >
                  <option value="date">Trier par date</option>
                  <option value="rating">Trier par note</option>
                </select>
              </div>
            </div>
          </div>
        </template>
        <template #results>
          <CardContainer
            v-if="viewMode === 'card'"
            :items="communityWaperOpinions"
            :no-result-text="noContent"
          />
          <WaperOpinionContainer
            v-else-if="viewMode === 'list'"
            :items="opinions"
          />
        </template>
      </SearchResults>
      <ThePagination
        :total-pages="communityWaperOpinionsPages"
        :current-page="communityWaperOpinionsCurrentPage"
        :go-to="changePageTo"
      />
    </div>
    <PegiWarning v-if="community?.pegi === '18'" />
  </div>
</template>

<script setup lang="ts">
definePageMeta({
  middleware: async (to) => {
    const loadPlayersReviewsCommunity = useLoadPlayersReviewsCommunity()
    return loadPlayersReviewsCommunity(to)
  },
})

const wapersOpinions = useWaperOpinionsStore()

const {
  communityWaperOpinions,
  communityWaperOpinionsPages,
  communityWaperOpinionsCurrentPage,
} = storeToRefs(wapersOpinions)

const link = useLinkPrevNext({
  currentPage: communityWaperOpinionsCurrentPage,
  pagesCount: communityWaperOpinionsPages,
})

const communitiesStore = useCommunitiesStore()
const { community } = storeToRefs(communitiesStore)

const arianeRoute = computed(() => {
  return [
    {
      title: 'Toutes les communautés',
      route: '/communautes',
    },
    {
      title: community.value?.title,
      route: `/communautes/${community.value?.slug}`,
    },
    {
      title: "L'avis des joueurs",
      route: '',
    },
  ]
})
const aggregateRatingSchema = useAggregateRatingSchema({
  community,
})

const breadcrumbSchema = useBreadcrumbSchema({
  arianeRoute,
})

useHead({
  title:
    'Avis, forum ' + community.value?.title + ' PS5, PS4, PlayStation 4 Pro',
  link,
  script: [aggregateRatingSchema, breadcrumbSchema],
})

useSeoMeta({
  title:
    'Avis, forum ' + community.value?.title + ' PS5, PS4, PlayStation 4 Pro',
  description: `Besoin d'avis de joueurs sur ${community.value?.title} ? Découvrez tous les avis des joueurs sur ${community.value?.title} sur We Are PlayStation.`,
  ogTitle: 'Tous les avis de joueurs sur ' + community.value?.title,
  ogDescription:
    'Retrouvez tous les avis de la communauté We Are PlayStation sur ' +
    community.value?.title,
  ogImage:
    community.value?.image_desktop?.url ??
    'https://www.weareplaystation.fr/img/og-img.jpg',
})

const referentialStore = useReferentialStore()
const { referential } = storeToRefs(referentialStore)

const mediaQuery = useCustomMediaQuery()
const currentPageInfo = computed(() => {
  const rawCommunity = unref(community)
  const image = getImage(rawCommunity, mediaQuery)

  return {
    title: `L'avis des joueurs`,
    game: rawCommunity?.title,
    pegi: rawCommunity?.pegi,
    members: rawCommunity?.users_count,
    image,
    contribPoints: referential.value?.points.post_review_create,
    description: `Retrouvez tous les avis de la communauté sur ${rawCommunity?.title}`,
  }
})

const opinions = computed(() => {
  const rawCommunity = unref(community)

  return communityWaperOpinions.value.map((item) => {
    const categories = []

    if (item.rating_graph) {
      categories.push({
        title: 'Graphisme',
        score: item.rating_graph,
      })
    }

    if (item.rating_gaming) {
      categories.push({
        title: 'Jouabilité',
        score: item.rating_gaming,
      })
    }

    if (item.rating_life) {
      categories.push({
        title: 'Durée de vie',
        score: item.rating_life,
      })
    }

    if (item.rating_design) {
      categories.push({
        title: 'Design',
        score: item.rating_design,
      })
    }

    const answers = []

    if (item.hours_played && rawCommunity?.type?.slug === 'jeu') {
      answers.push({
        label: 'Heures jouées sur ce jeu',
        answer:
          referential.value?.hoursPlayed.find(
            (i) => i.value === item.hours_played,
          )?.label ?? 'Autres',
      })
    }

    if (item.market_place && rawCommunity?.type?.slug === 'jeu') {
      answers.push({
        label: "Lieu d'achat du jeu",
        answer:
          referential.value?.choiceMarketPlace.find(
            (i) => i.value === item.market_place,
          )?.label ?? 'Autres',
      })
    }

    if (rawCommunity?.type?.slug === 'service') {
      answers.push(
        {
          label: "Ce produit est-il facile d'utilisation ?",
          answer: item.ez_to_use_service ? 'Oui' : 'Non',
        },
        {
          label:
            'Le contenu proposé par ce service correspond-il à vos attentes ?',
          answer: item.meet_expectations ? 'Oui' : 'Non',
        },
      )
    }

    if (rawCommunity?.type?.slug === 'console') {
      answers.push(
        {
          label: "Ce produit est-il facile d'utilisation ?",
          answer: item.ez_to_use_product ? 'Oui' : 'Non',
        },
        {
          label:
            'Les services disponibles sur ce produit correspondent-ils à vos attentes ?',
          answer: item.meet_expectations_product ? 'Oui' : 'Non',
        },
      )
    }

    if (
      rawCommunity?.type?.slug === 'service' ||
      rawCommunity?.type?.slug === 'console'
    ) {
      let answer = ''

      switch (item.ps_plus) {
        case 0:
          answer = 'Aucune'
          break
        case 1:
          answer = 'PlayStation Plus Essential'
          break
        case 2:
          answer = 'PlayStation Plus Extra'
          break
        case 3:
          answer = 'PlayStation Plus Premium'
          break
      }

      answers.push({
        label: 'À quelle offre PlayStation Plus êtes-vous abonné ?',
        answer,
      })
    }

    if (rawCommunity?.type?.slug === 'console') {
      answers.push({
        label: 'Êtes-vous équipé d’une TV 4K ?',
        answer: item.tv4k ? 'Oui' : 'Non',
      })
    }

    if (item.user_created_at) {
      answers.push({
        label: 'Membre inscrit depuis le',
        answer: dateToString(item.user_created_at),
      })
    }

    return {
      id: item.id,
      user: item.user,
      route: `/communautes/${rawCommunity?.slug}/avis-joueurs/detail/${item.slug}`,
      slug: item.slug,
      score: item.rating,
      title: item.title,
      ps_version: item.ps_version,
      description: item.content,
      recommendation: item.is_recommended,
      categories,
      medias: item.medias,
      answers,
    }
  })
})

const communityStats = computed(() => {
  const rawCommunity = unref(community)

  if (!rawCommunity?.stats) return
  const categories = []

  if (rawCommunity?.stats.rating_graph > 0) {
    categories.push({
      title: 'Graphisme',
      score: rawCommunity?.stats.rating_graph,
    })
  }

  if (rawCommunity?.stats.rating_gaming > 0) {
    categories.push({
      title: 'Jouabilité',
      score: rawCommunity?.stats.rating_gaming,
    })
  }

  if (rawCommunity?.stats.rating_life > 0) {
    categories.push({
      title: 'Durée de vie',
      score: rawCommunity?.stats.rating_life,
    })
  }

  if (rawCommunity?.stats.rating_design > 0) {
    categories.push({
      title: 'Design',
      score: rawCommunity?.stats.rating_design,
    })
  }

  let scores: { score: number; percent: number }[] = []

  if (rawCommunity?.stats.rating_distribution_percent) {
    scores = [
      {
        score: 5,
        percent: rawCommunity?.stats.rating_distribution_percent['5'],
      },
      {
        score: 4,
        percent: rawCommunity?.stats.rating_distribution_percent['4'],
      },
      {
        score: 3,
        percent: rawCommunity?.stats.rating_distribution_percent['3'],
      },
      {
        score: 2,
        percent: rawCommunity?.stats.rating_distribution_percent['2'],
      },
      {
        score: 1,
        percent: rawCommunity?.stats.rating_distribution_percent['1'],
      },
    ]
  }

  return {
    recommendation: rawCommunity?.stats.recommendations_percent,
    totalScore: rawCommunity?.stats.rating,
    scores,
    categories,
    total_review: rawCommunity?.stats.total_review,
    slug: rawCommunity?.slug,
    communityType: rawCommunity?.type?.slug,
  }
})

const viewMode = ref('list')
const sortReview = ref('date')
const route = useRoute()

const searchData = async () => {
  isLoading.value = true

  await wapersOpinions.fetchCommunityWaperOpinions({
    page: 1,
    search: search.value,
    sort: sortReview.value,
    community: route.params.communityName as string,
  })

  if (!communityWaperOpinions.value.length) {
    noContent.value = !search.value
      ? 'Aucun contenu'
      : 'Aucun résultat pour votre recherche'
  }

  isLoading.value = false
}

const { isLoading, search, searchUpdateHandler, noContent } = useSearching({
  searchData,
})

const onSortChange = async () => {
  await wapersOpinions.fetchCommunityWaperOpinions({
    page: 1,
    search: search.value,
    sort: sortReview.value,
    community: route.params.communityName as string,
  })

  if (!communityWaperOpinions.value.length) {
    if (search.value === '') {
      noContent.value = 'Aucun contenu'
    } else {
      noContent.value = 'Aucun résultat pour votre recherche'
    }
  }
}

const changePageTo = async (index = 1) => {
  if (index > 0) {
    await wapersOpinions.fetchCommunityWaperOpinions({
      page: index,
      search: search.value,
      sort: sortReview.value,
      community: route.params.communityName as string,
    })
  }
}

watch(search, (newSearch) => {
  searchUpdateHandler(newSearch)
})
</script>

<style lang="scss" scoped>
.waper-opinions-page {
  min-height: 100vh;
  padding-bottom: 10rem;

  .title-line {
    display: flex;
    flex-grow: 1;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0;
  }

  .options {
    display: flex;
    align-items: center;

    .sort {
      margin-left: 2rem;

      select {
        padding-right: 2rem;
        font-size: 1.2rem;
        background-position: right center;
        border: 0;

        &:focus {
          box-shadow: none;
        }
      }
    }
  }

  .bazar {
    position: relative;
  }

  .mode {
    display: flex;
    align-items: center;

    p {
      margin-right: 2rem;
      font-size: 1.5rem;
      font-weight: 500;
      line-height: 1.9rem;
      letter-spacing: 0.15rem;
    }

    .mode-button {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 4rem;
      height: 3rem;
      margin-right: 1rem;
      background: #ccc;

      &.active {
        background: $blue;

        svg {
          line {
            stroke: $white;
          }

          rect {
            fill: $white;
          }
        }
      }

      &.list {
        svg {
          width: 1.66rem;
          height: 1.22rem;
        }
      }

      &.bloc {
        svg {
          width: 1.67rem;
          height: 1.67rem;
        }
      }
    }
  }
}

@media screen and (max-width: 991px) {
  .waper-opinions-page {
    .title-line {
      flex-direction: column;
      align-items: flex-start;
      margin-bottom: 2rem;

      .mode {
        margin-bottom: 1rem;
      }

      .options {
        width: 100%;
      }
    }
  }
}
</style>
